import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { QcFieldType, QcPrefill } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MAX_QC_OPTIONS, NO_PREFILL, type QcFormInput } from '../schemas/qc-form-schema';

export interface QcFieldCardProps {
  index: number;
  total: number;
  onMove: (to: number) => void;
  onRemove: () => void;
  disabled?: boolean;
}

function OptionsEditor({
  index,
  disabled = false,
}: {
  index: number;
  disabled?: boolean | undefined;
}) {
  const { t } = useTranslation('workflows');
  const form = useFormContext<QcFormInput>();
  const name = `fields.${index}.options` as const;
  const { fields, append, remove } = useFieldArray({ control: form.control, name });
  const error = form.formState.errors.fields?.[index]?.options;
  const arrayError = error?.message ?? error?.root?.message;

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-medium">{t('qc.field.options')}</legend>
      <ul className="flex flex-col gap-2">
        {fields.map((item, optionIndex) => (
          <li key={item.id} className="flex items-start gap-2">
            <FormField
              control={form.control}
              name={`${name}.${optionIndex}.value`}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={120}
                      aria-label={t('qc.field.option', { index: optionIndex + 1 })}
                      disabled={disabled}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('qc.field.removeOption', { index: optionIndex + 1 })}
              disabled={disabled}
              onClick={() => remove(optionIndex)}
            >
              <X aria-hidden />
            </Button>
          </li>
        ))}
      </ul>
      {arrayError ? (
        <p role="alert" className="text-destructive text-sm">
          {arrayError}
        </p>
      ) : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        disabled={disabled || fields.length >= MAX_QC_OPTIONS}
        onClick={() => append({ value: '' })}
      >
        <Plus aria-hidden />
        {t('qc.field.addOption')}
      </Button>
      <FormField
        control={form.control}
        name={`fields.${index}.allowOther`}
        render={({ field }) => (
          <FormItem className="flex flex-row items-center gap-2">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={(checked) => field.onChange(checked === true)}
                disabled={disabled}
              />
            </FormControl>
            <FormLabel className="font-normal">{t('qc.field.allowOther')}</FormLabel>
          </FormItem>
        )}
      />
    </fieldset>
  );
}

/** One question of the checklist: wording, answer type, required, prefill and choice options. */
export function QcFieldCard({
  index,
  total,
  onMove,
  onRemove,
  disabled = false,
}: QcFieldCardProps) {
  const { t } = useTranslation('workflows');
  const form = useFormContext<QcFormInput>();
  const prefix = `fields.${index}` as const;
  const type = useWatch({ control: form.control, name: `${prefix}.type` });
  const label =
    useWatch({ control: form.control, name: `${prefix}.label` }).trim() || t('qc.field.untitled');
  const hasError = Boolean(form.formState.errors.fields?.[index]);

  return (
    <li
      aria-label={t('qc.field.label', { index: index + 1, label })}
      className={cn(
        'bg-card flex flex-col gap-4 rounded-lg border p-4',
        hasError && 'border-destructive/60',
      )}
    >
      <div className="flex items-start gap-2">
        <span className="bg-muted text-muted-foreground mt-6 flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-medium tabular-nums">
          {index + 1}
        </span>
        <FormField
          control={form.control}
          name={`${prefix}.label`}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormLabel>{t('qc.field.question')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  maxLength={200}
                  placeholder={t('qc.field.questionPlaceholder')}
                  disabled={disabled}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="mt-6 flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('qc.field.moveUp', { label })}
            disabled={disabled || index === 0}
            onClick={() => onMove(index - 1)}
          >
            <ArrowUp aria-hidden />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('qc.field.moveDown', { label })}
            disabled={disabled || index === total - 1}
            onClick={() => onMove(index + 1)}
          >
            <ArrowDown aria-hidden />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('qc.field.remove', { label })}
            disabled={disabled}
            onClick={onRemove}
          >
            <Trash2 aria-hidden />
          </Button>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <FormField
          control={form.control}
          name={`${prefix}.type`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('qc.field.type')}</FormLabel>
              <Select
                value={field.value}
                disabled={disabled}
                onValueChange={(next) => {
                  field.onChange(next);
                  if (next === 'choice' && form.getValues(`${prefix}.options`).length === 0) {
                    form.setValue(`${prefix}.options`, [{ value: '' }], { shouldDirty: true });
                  }
                  if (next === 'file')
                    form.setValue(`${prefix}.prefill`, NO_PREFILL, { shouldDirty: true });
                }}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.values(QcFieldType).map((option) => (
                    <SelectItem key={option} value={option}>
                      {t(`qc.fieldType.${option}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        {type === 'file' ? null : (
          <FormField
            control={form.control}
            name={`${prefix}.prefill`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('qc.field.prefill')}</FormLabel>
                <Select value={field.value} onValueChange={field.onChange} disabled={disabled}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {[NO_PREFILL, ...Object.values(QcPrefill)].map((option) => (
                      <SelectItem key={option} value={option}>
                        {t(`qc.prefill.${option}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>{t('qc.field.prefillHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name={`${prefix}.required`}
          render={({ field }) => (
            <FormItem className="flex flex-row items-center gap-2 sm:pt-7">
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={disabled}
                />
              </FormControl>
              <FormLabel className="font-normal">{t('qc.field.required')}</FormLabel>
            </FormItem>
          )}
        />
      </div>
      {type === 'choice' ? <OptionsEditor index={index} disabled={disabled} /> : null}
      {type === 'file' ? (
        <p className="text-muted-foreground text-xs">{t('qc.field.fileHint')}</p>
      ) : null}
      <FormField
        control={form.control}
        name={`${prefix}.key`}
        render={({ field }) => (
          <FormItem className="sm:max-w-xs">
            <FormLabel>{t('qc.field.key')}</FormLabel>
            <FormControl>
              <Input {...field} className="font-mono" maxLength={60} disabled={disabled} />
            </FormControl>
            <FormDescription>{t('qc.field.keyHint')}</FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
    </li>
  );
}
